import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ChevronDown, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  cancelInvite,
  changeRole,
  deactivatePerson,
  deleteRole,
  getRoles,
  getTeam,
  invitePerson,
  reactivatePerson,
  resendInvite,
} from './teamApi';
import TeamsTab from './TeamsTab';
import RoleEditor, { SCOPE_LABELS } from './RoleEditor';
import { useCompanyAccess } from '../../Context/CompanyAccessContext';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const peopleLabel = (count) => (count === 0 ? 'Nobody' : `${count} ${count === 1 ? 'person' : 'people'}`);

const selectClass =
  'h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60';

const errorMessage = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

const initialsFor = (name) => {
  const parts = (name || '?').trim().split(/\s+/).filter(Boolean);
  const seed = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
  return seed.toUpperCase();
};

const lastActive = (iso) => {
  if (!iso) return 'Never signed in';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 2) return 'Active just now';
  if (minutes < 60) return `Active ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Active ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Active ${days}d ago`;
  return `Last active ${new Date(iso).toLocaleDateString()}`;
};

const statusOf = (person) => {
  if (person.status === 'deactivated') {
    return { label: 'Deactivated', className: 'bg-slate-100 text-slate-600' };
  }
  if (person.status !== 'invited') {
    return { label: 'Active', className: 'bg-emerald-50 text-emerald-700' };
  }
  const expired = person.invite_expires_at && new Date(person.invite_expires_at) < new Date();
  return expired
    ? { label: 'Invite expired', className: 'bg-red-50 text-red-700' }
    : { label: 'Invited', className: 'bg-amber-50 text-amber-700' };
};

const SeatMeter = ({ seats }) => {
  const { used, limit } = seats;
  if (limit === null || limit === undefined) {
    return <p className="text-xs text-muted-foreground">{used} seats used. Your plan has no seat limit.</p>;
  }
  const full = used >= limit;
  return (
    <div className="min-w-0 flex-1">
      <p className={cn('text-xs', full ? 'font-medium text-amber-700' : 'text-muted-foreground')}>
        {used} of {limit} seats used
      </p>
      <div
        className="mt-1 h-1 w-full max-w-[220px] rounded-full bg-slate-100"
        role="progressbar"
        aria-label="Seats used"
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={Math.min(used, limit)}
      >
        <div
          className={cn('h-1 rounded-full', full ? 'bg-amber-500' : 'bg-primary')}
          style={{ width: `${Math.min(100, limit ? (used / limit) * 100 : 100)}%` }}
        />
      </div>
    </div>
  );
};

/**
 * One row. Status and actions read the same across every person. `manageable` is
 * false for someone whose role can do more than yours: the server refuses those
 * changes, so the row doesn't offer them.
 */
const PersonRow = ({ person, assignableRoles, busy, manageable = true, onRoleChange, onAction }) => {
  const status = statusOf(person);
  const canChangeRole = !person.is_you && person.status !== 'deactivated' && manageable;
  const roleOptions = assignableRoles.some((role) => role.key === person.role.key)
    ? assignableRoles
    : [{ key: person.role.key, name: person.role.name }, ...assignableRoles];

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
        {initialsFor(person.name)}
      </div>
      <div className="min-w-0 flex-1 basis-40">
        <div className="truncate text-sm font-medium text-slate-900">
          {person.name}
          {person.is_you ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">You</span> : null}
        </div>
        <div className="truncate text-xs text-muted-foreground">{person.email}</div>
      </div>

      <div className="w-48 shrink-0">
        {canChangeRole ? (
          <select
            aria-label={`Role for ${person.name}`}
            className={cn(selectClass, 'w-full')}
            value={person.role.key}
            disabled={busy}
            onChange={(e) => onRoleChange(person, e.target.value)}
          >
            {roleOptions.map((role) => (
              <option key={role.key} value={role.key}>{role.name}</option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-slate-700">{person.role.name}</span>
        )}
      </div>

      <div className="w-32 shrink-0">
        <span className={cn('inline-block rounded-full px-2 py-0.5 text-xs font-medium', status.className)}>
          {status.label}
        </span>
        {person.status === 'active' ? (
          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{lastActive(person.last_login_at)}</div>
        ) : null}
      </div>

      <div className="ml-auto flex w-40 shrink-0 items-center justify-end gap-1">
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Saving" /> : null}
        {person.status === 'invited' ? (
          <>
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onAction('resend', person)}>
              Resend
            </Button>
            {manageable ? (
              <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onAction('cancel', person)}
                className="text-slate-500 hover:text-red-600">
                Cancel
              </Button>
            ) : null}
          </>
        ) : null}
        {person.status === 'active' && !person.is_you && manageable ? (
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onAction('deactivate', person)}
            className="text-slate-500 hover:text-red-600">
            Deactivate
          </Button>
        ) : null}
        {person.status === 'deactivated' && manageable ? (
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onAction('reactivate', person)}>
            Reactivate
          </Button>
        ) : null}
      </div>
    </li>
  );
};

const InviteForm = ({ roles, onInvited, onClose }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState(roles[0]?.key || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selected = roles.find((role) => role.key === roleKey);

  const submit = async (e) => {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!fullName.trim()) {
      setError({ message: "Enter the person's name." });
      return;
    }
    if (!EMAIL_REGEX.test(address)) {
      setError({ message: 'Enter a valid email address.' });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await invitePerson({ email: address, fullName: fullName.trim(), roleKey });
      await onInvited(result, address);
    } catch (err) {
      setError({
        message: errorMessage(err, 'Could not send the invite.'),
        upgrade: Boolean(err?.response?.data?.upgrade_required),
      });
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3" noValidate>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_12rem]">
        <div>
          <Label htmlFor="team-invite-name" className="text-xs">Name</Label>
          <Input id="team-invite-name" className="h-9" value={fullName} autoFocus disabled={saving}
            onChange={(e) => setFullName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="team-invite-email" className="text-xs">Work email</Label>
          <Input id="team-invite-email" type="email" className="h-9" value={email} disabled={saving}
            onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="team-invite-role" className="text-xs">Role</Label>
          <select id="team-invite-role" className={cn(selectClass, 'h-9 w-full')} value={roleKey} disabled={saving}
            onChange={(e) => setRoleKey(e.target.value)}>
            {roles.map((role) => <option key={role.key} value={role.key}>{role.name}</option>)}
          </select>
        </div>
      </div>
      {selected ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {selected.uses_seat ? 'Uses a seat on your plan.' : "Doesn't use a seat."}
          {' '}They get an email link to set a password. It expires in 72 hours.
        </p>
      ) : null}
      {error ? (
        <div role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
          {error.upgrade ? (
            <Link to="/hirewithus/billing" className="ml-1 font-medium underline">View plans</Link>
          ) : null}
        </div>
      ) : null}
      <div className="mt-3 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Send invite
        </Button>
      </div>
    </form>
  );
};

const PeopleTab = ({ people, seats, roles, canGive, loading, reload }) => {
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const assignableRoles = useMemo(() => roles.filter((role) => role.assignable && canGive(role)), [roles, canGive]);
  const roleByKey = useMemo(() => new Map(roles.map((role) => [role.key, role])), [roles]);
  const manageable = (person) => {
    const role = roleByKey.get(person.role.key);
    return !role || canGive(role);
  };

  const run = useCallback(async (person, work, success) => {
    setBusyId(person.id);
    try {
      const result = await work();
      if (result && result.email_sent === false) {
        toast.warning("Saved, but the email didn't send. Use Resend to try again.");
      } else {
        toast.success(success);
      }
      await reload();
    } catch (err) {
      toast.error(errorMessage(err, "That didn't work. Please try again."));
    } finally {
      setBusyId(null);
    }
  }, [reload]);

  const handleInvited = async (result, address) => {
    if (result?.email_sent === false) {
      toast.warning("Invite created, but the email didn't send. Use Resend to try again.");
    } else {
      toast.success(`Invite sent to ${address}`);
    }
    setInviting(false);
    await reload();
  };

  const handleRoleChange = (person, roleKey) =>
    run(person, () => changeRole(person.id, roleKey), `${person.name}'s role was updated`);

  const handleAction = (action, person) => {
    if (action === 'resend') {
      run(person, () => resendInvite(person.id), `Invite sent again to ${person.email}`);
    } else if (action === 'reactivate') {
      run(person, () => reactivatePerson(person.id), `${person.name} can sign in again`);
    } else {
      setConfirming({ action, person });
    }
  };

  const confirmAction = () => {
    const { action, person } = confirming;
    setConfirming(null);
    if (action === 'deactivate') {
      run(person, () => deactivatePerson(person.id), `${person.name} was deactivated`);
    } else {
      run(person, () => cancelInvite(person.id), `Invite to ${person.email} was cancelled`);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SeatMeter seats={seats} />
        {!inviting ? (
          <Button type="button" size="sm" onClick={() => setInviting(true)} disabled={assignableRoles.length === 0}>
            <Plus className="mr-1.5 h-4 w-4" />
            Invite person
          </Button>
        ) : null}
      </div>

      {inviting ? (
        <InviteForm roles={assignableRoles} onInvited={handleInvited} onClose={() => setInviting(false)} />
      ) : null}

      <div className="rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading team" />
          </div>
        ) : people.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-medium text-slate-700">Nobody else on your team yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Invite colleagues so each person signs in with their own login and role.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {people.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                assignableRoles={assignableRoles}
                busy={busyId === person.id}
                manageable={manageable(person)}
                onRoleChange={handleRoleChange}
                onAction={handleAction}
              />
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={Boolean(confirming)} onOpenChange={(open) => { if (!open) setConfirming(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming?.action === 'deactivate'
                ? `Deactivate ${confirming?.person.name}?`
                : `Cancel the invite to ${confirming?.person.email}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.action === 'deactivate'
                ? "They're signed out within 30 seconds and can't sign in again. Their past work stays in place, and you can reactivate them later."
                : "The link in their email stops working. You can invite them again any time."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={confirmAction}>
              {confirming?.action === 'deactivate' ? 'Deactivate' : 'Cancel invite'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const RoleRow = ({ role, permissions, isYours = false, actions = null }) => {
  const [open, setOpen] = useState(false);
  const held = useMemo(() => new Set(role.permissions), [role.permissions]);
  const areas = useMemo(() => {
    const byArea = new Map();
    for (const permission of permissions) {
      if (!byArea.has(permission.area)) byArea.set(permission.area, []);
      byArea.get(permission.area).push(permission);
    }
    return [...byArea.entries()];
  }, [permissions]);

  return (
    <li>
      <div className="flex items-center hover:bg-slate-50">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <span className="min-w-0 flex-1 basis-40 truncate text-sm font-medium text-slate-900">
            {role.name}
            {role.custom ? (
              <span className="ml-1.5 rounded-full bg-sky-50 px-1.5 py-px text-[10px] font-medium text-sky-700">Custom</span>
            ) : null}
            {isYours ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">Your role</span> : null}
          </span>
          <span className="w-36 shrink-0 text-xs text-muted-foreground">{SCOPE_LABELS[role.scope] || role.scope}</span>
          <span className="w-24 shrink-0 text-xs text-muted-foreground">{role.permissions.length} permissions</span>
          <span className="w-20 shrink-0 text-xs text-muted-foreground">{role.uses_seat ? 'Uses a seat' : 'Free'}</span>
          {typeof role.member_count === 'number' ? (
            <span className="w-20 shrink-0 text-xs text-muted-foreground">{peopleLabel(role.member_count)}</span>
          ) : null}
          {role.assignable ? null : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Not available yet</span>
          )}
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
        {actions ? <div className="flex w-36 shrink-0 items-center justify-end gap-1 pr-2">{actions}</div> : null}
      </div>
      {open ? (
        <div className="grid gap-x-6 gap-y-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3 sm:grid-cols-2">
          {areas.map(([area, entries]) => (
            <div key={area}>
              <div className="mb-1 text-xs font-semibold text-slate-700">{area}</div>
              <ul className="space-y-0.5">
                {entries.map((permission) => {
                  const has = held.has(permission.key);
                  return (
                    <li key={permission.key} className={cn('flex items-start gap-1.5 text-xs', has ? 'text-slate-700' : 'text-slate-400')}>
                      {has
                        ? <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" aria-label="Included" />
                        : <X className="mt-0.5 h-3 w-3 shrink-0" aria-label="Not included" />}
                      <span>{permission.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </li>
  );
};

/** Deleting a role someone has moves them to another role first, in the same step. */
const DeleteRoleDialog = ({ role, roles, canGive, onClose, onDeleted }) => {
  const [moveTo, setMoveTo] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setMoveTo('');
    setError('');
  }, [role]);

  const inUse = (role?.member_count || 0) > 0;
  const targets = roles.filter((r) => r.key !== role?.key && r.assignable && canGive(r));

  const confirm = async () => {
    setWorking(true);
    setError('');
    try {
      const result = await deleteRole(role.key, inUse ? moveTo : undefined);
      const target = roles.find((r) => r.key === moveTo);
      toast.success(result?.moved
        ? `${role.name} was deleted. ${peopleLabel(result.moved)} moved to ${target?.name || 'the new role'}.`
        : `${role.name} was deleted`);
      await onDeleted();
    } catch (err) {
      setError(errorMessage(err, 'Could not delete the role.'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <AlertDialog open={Boolean(role)} onOpenChange={(open) => { if (!open && !working) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {role?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            {inUse
              ? `${peopleLabel(role.member_count)} ${role.member_count === 1 ? 'has' : 'have'} this role (deactivated people count too). They move to the role you choose, straight away.`
              : "Nobody has this role. This can't be undone."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {inUse ? (
          <div>
            <Label htmlFor="role-move-to" className="text-xs">Move them to</Label>
            <select id="role-move-to" className={cn(selectClass, 'h-9 w-full')} value={moveTo} disabled={working}
              onChange={(e) => setMoveTo(e.target.value)}>
              <option value="">Choose a role</option>
              {targets.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
          </div>
        ) : null}
        {error ? (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={working}>Keep</AlertDialogCancel>
          <Button type="button" variant="destructive" disabled={working || (inUse && !moveTo)} onClick={confirm}>
            {working ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {inUse ? 'Move and delete' : 'Delete role'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * The built-in roles, read-only, and the company's own, which it can build from the
 * same permissions. A server without custom roles sends no freePermissions, and then
 * this stays read-only.
 */
const RolesTab = ({ roles, permissions, freePermissions, you, maxCustomRoles, canGive, loading, onChanged }) => {
  const [editing, setEditing] = useState(null);   // { role } to edit, { start } to duplicate, {} for new
  const [deleting, setDeleting] = useState(null);
  const editable = Array.isArray(freePermissions);
  const customCount = roles.filter((role) => role.custom).length;
  const atLimit = maxCustomRoles !== null && maxCustomRoles !== undefined && customCount >= maxCustomRoles;

  const afterSave = async () => {
    setEditing(null);
    await onChanged();
  };

  if (editing) {
    return (
      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => setEditing(null)}
          className="w-fit text-xs text-muted-foreground hover:text-foreground">
          ← Back to roles
        </button>
        <RoleEditor
          role={editing.role || null}
          start={editing.start || null}
          permissions={permissions}
          freePermissions={freePermissions}
          grantable={you?.grantable || null}
          onSaved={afterSave}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  const actionsFor = (role) => {
    if (!editable) return null;
    if (!role.custom) {
      return (
        <Button type="button" variant="ghost" size="sm" disabled={atLimit} onClick={() => setEditing({ start: role })}
          aria-label={`Duplicate ${role.name}`}>
          Duplicate
        </Button>
      );
    }
    const yours = you?.role === role.key;
    const blocked = yours ? 'This is your role.' : canGive(role) ? '' : 'This role can do things yours can’t.';
    return (
      <>
        <Button type="button" variant="ghost" size="sm" disabled={Boolean(blocked)} title={blocked || undefined}
          onClick={() => setEditing({ role })} aria-label={`Edit ${role.name}`}>
          Edit
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={Boolean(blocked)} title={blocked || undefined}
          onClick={() => setDeleting(role)} aria-label={`Delete ${role.name}`}
          className="text-slate-500 hover:text-red-600">
          Delete
        </Button>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {editable
            ? 'What each role can do. Make your own from the same permissions, or duplicate a built-in role to start. The company owner always has full access.'
            : "What each role can do. Roles can't be edited yet. The company owner always has full access."}
        </p>
        {editable ? (
          <Button type="button" size="sm" disabled={atLimit} onClick={() => setEditing({})}
            title={atLimit ? `A company can have up to ${maxCustomRoles} custom roles.` : undefined}>
            <Plus className="mr-1.5 h-4 w-4" />
            New role
          </Button>
        ) : null}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading roles" />
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {roles.map((role) => (
              <RoleRow key={role.key} role={role} permissions={permissions} isYours={you?.role === role.key}
                actions={actionsFor(role)} />
            ))}
          </ul>
        )}
      </div>
      <DeleteRoleDialog
        role={deleting}
        roles={roles}
        canGive={canGive}
        onClose={() => setDeleting(null)}
        onDeleted={async () => {
          setDeleting(null);
          await onChanged();
        }}
      />
    </div>
  );
};

/** Settings → Team & Roles: who has a login at this company and what they can do. */
const TeamSection = () => {
  const { scopedRolesEnabled } = useCompanyAccess();
  const [tab, setTab] = useState('people');
  const [people, setPeople] = useState([]);
  const [seats, setSeats] = useState({ used: 0, limit: null });
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [roleInfo, setRoleInfo] = useState({ freePermissions: null, you: null, maxCustomRoles: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Nobody hands out a role, or manages someone on one, that can do more than their
  // own. The Owner can give everything; an older server sends no list, so nothing is held back.
  const canGive = useCallback((role) => {
    const grantable = roleInfo.you?.grantable;
    return !grantable || role.permissions.every((key) => grantable.includes(key));
  }, [roleInfo]);

  const showRoles = useCallback((catalog) => {
    setRoles(catalog.roles);
    setPermissions(catalog.permissions);
    setRoleInfo({ freePermissions: catalog.freePermissions, you: catalog.you, maxCustomRoles: catalog.maxCustomRoles });
  }, []);

  const reload = useCallback(async () => {
    try {
      const team = await getTeam();
      if (!mounted.current) return;
      setPeople(team.people);
      setSeats(team.seats);
      setError('');
    } catch (err) {
      if (mounted.current) setError(errorMessage(err, 'Could not load your team.'));
    }
  }, []);

  /** After a role changes: the roles, and the people on them. */
  const reloadAll = useCallback(async () => {
    try {
      const [team, catalog] = await Promise.all([getTeam(), getRoles()]);
      if (!mounted.current) return;
      setPeople(team.people);
      setSeats(team.seats);
      showRoles(catalog);
      setError('');
    } catch (err) {
      if (mounted.current) setError(errorMessage(err, 'Could not load your team.'));
    }
  }, [showRoles]);

  useEffect(() => {
    (async () => {
      try {
        const [team, catalog] = await Promise.all([getTeam(), getRoles()]);
        if (!mounted.current) return;
        setPeople(team.people);
        setSeats(team.seats);
        showRoles(catalog);
      } catch (err) {
        if (mounted.current) setError(errorMessage(err, 'Could not load your team.'));
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
  }, [showRoles]);

  const tabs = [
    { key: 'people', label: 'People' },
    ...(scopedRolesEnabled ? [{ key: 'teams', label: 'Teams' }] : []),
    { key: 'roles', label: 'Roles' },
  ];

  return (
    <section aria-label="Team and roles" className="flex flex-col gap-3">
      <div className="flex gap-1" role="tablist" aria-label="Team and roles views">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            id={`team-tab-${item.key}`}
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === item.key ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {tab === 'people' ? (
        <PeopleTab people={people} seats={seats} roles={roles} canGive={canGive} loading={loading} reload={reload} />
      ) : tab === 'teams' ? (
        <TeamsTab />
      ) : (
        <RolesTab
          roles={roles}
          permissions={permissions}
          freePermissions={roleInfo.freePermissions}
          you={roleInfo.you}
          maxCustomRoles={roleInfo.maxCustomRoles}
          canGive={canGive}
          loading={loading}
          onChanged={reloadAll}
        />
      )}
    </section>
  );
};

export default TeamSection;
